import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { ResourceSetContext } from '../context/ResourceSetContext';
import { useLanguage } from '../context/LanguageContext';
import { useSchema } from '../context/SchemaContext';

const HelmetWrapper = ({
  title,
  description,
  image,
  altText,
  url,
  schemaJsonLd,
  noIndex,
  noFollow,
  ...rest
}) => {
  const context = useContext(ResourceSetContext);
  const globalRS = context.getResourcesForComponent('global');
  const lang = useLanguage();
  const { additionalSchemas, clearSchemas } = useSchema();
  
  // Combine main schema with additional schemas from components
  const combinedSchema = React.useMemo(() => {
    if (!schemaJsonLd && additionalSchemas.length === 0) return null;
    
    // Clean additional schemas - remove @context since they'll be in @graph
    const cleanedAdditionalSchemas = additionalSchemas.map(schema => {
      const { '@context': context, ...cleanSchema } = schema;
      return cleanSchema;
    });
    
    if (!schemaJsonLd) {
      // Only additional schemas
      return cleanedAdditionalSchemas.length === 1 
        ? cleanedAdditionalSchemas[0] 
        : {
            "@context": "https://schema.org",
            "@graph": cleanedAdditionalSchemas
          };
    }
    
    if (cleanedAdditionalSchemas.length === 0) {
      // Only main schema
      return schemaJsonLd;
    }
    
    // Combine main schema with additional schemas
    const mainGraph = schemaJsonLd["@graph"] || [schemaJsonLd];
    return {
      "@context": "https://schema.org",
      "@graph": [...mainGraph, ...cleanedAdditionalSchemas]
    };
  }, [schemaJsonLd, additionalSchemas]);
  
  // Clear schemas on unmount to prevent leakage between pages
  React.useEffect(() => {
    return () => clearSchemas();
  }, [clearSchemas]);
  
  return (
    <Helmet
      defaultTitle={title ? title : globalRS.websiteName}
      // Set language based on language of page
      htmlAttributes={{ lang: lang ?? 'en-US' }}
    >
      {description && (
        <meta property="description" content={description} name="description" />
      )}
      
      {lang === 'en-US' ? (
        // Only show on English pages
        <link rel="canonical" href={url} />
      ) : (
        /* Add noindex to translated pages */
        <meta name="robots" content="noindex" />
      )}
      <meta
        name="google-site-verification"
        content="XQZqnHjyspW4j6ulYMxd0-M3QTRt7vVcm4woeQY2ypo"
      />
      {noIndex && lang === 'en-US' ? <meta name="robots" content="noindex" /> : <meta name="robots" content="index"/>}
      {noFollow && lang === 'en-US' ? <meta name="robots" content="nofollow" /> : <meta name="robots" content="follow"/>}

      <meta name="docsearch:version" content="2.0" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover"
      />
      <meta
        property="og:title"
        content={title ? title : globalRS.websiteName}
      />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={globalRS.projectType} />
      <meta property="og:site_name" content={globalRS.websiteName} />
      <meta property="og:image" itemprop="image" content={image} />
      <meta property="og:image:alt" content={altText} />
      <meta property="og:description" content={description} />

      <meta
        property="twitter:image"
        name="twitter:image"
        content={`https:${image}`}
      />
      <meta
        property="twitter:site"
        name="twitter:site"
        content={globalRS.websiteName}
      />
      <meta
        property="twitter:title"
        name="twitter:title"
        content={title ? title : globalRS.websiteName}
      />
      {description && (
        <meta
          property="twitter:description"
          name="twitter:description"
          content={description}
        />
      )}
      <meta name="twitter:card" content="summary_large_image" />

      {combinedSchema && (
        <script type="application/ld+json">
          {JSON.stringify(combinedSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default HelmetWrapper;